/**
 * Workspace HTTP client (onboarding wizard, Settings > Workspace tab,
 * "+ Add New Workspace").
 *
 * Promise wrappers that resolve a `{ ok, data | error, status, nameTaken }`
 * result so callers can `await` without try/catch.  `status` is the HTTP
 * status of a failed request (`0` when the request never got a response) and
 * `nameTaken` marks the 409 the two workspace routes answer with when the
 * name is already in use.  Backend routes:
 * `backend/app/routes/workspaces_route.py` (`POST /workspaces`,
 * `PATCH /workspaces/{id}`) and `backend/app/routes/users_route.py`
 * (`POST /users/set-profile`).
 */
import { data_from_server_post, get_base_url } from '@/shared/http/client';
import { formatApiErrorDetail } from '@/shared/utils/formatApiErrorDetail';

const GENERIC_ERROR = 'Something went wrong. Please try again.';

const errorText = (error) => {
    if (typeof error === 'string' && error.trim()) return error;
    if (error && typeof error.message === 'string' && error.message.trim()) return error.message;
    return GENERIC_ERROR;
};

const post = (suffix, body) =>
    new Promise((resolve) => {
        data_from_server_post(
            suffix,
            body,
            (data) => resolve({ ok: true, data }),
            (error, cause) => resolve({ ok: false, error: errorText(error), status: Number(cause?.status) || 0 }),
        );
    });

/**
 * `PATCH` JSON to the backend.  The shared http client has no PATCH helper,
 * so this mirrors `data_from_server_post` (same base URL, cookie credentials,
 * `detail`-first error extraction) without the in-flight de-duplication,
 * which would collapse two different edits of the same field.
 */
const patch = async (suffix, body) => {
    const url = `${get_base_url(window.location.origin)}/${suffix}`;
    let response;
    try {
        response = await fetch(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            credentials: 'include',
        });
    } catch (error) {
        return { ok: false, error: errorText(error), status: 0 };
    }
    if (!response.ok) {
        let detail = '';
        try {
            const payload = await response.json();
            detail = formatApiErrorDetail(payload?.detail || payload?.message || '');
        } catch (_parseError) {
            detail = '';
        }
        return {
            ok: false,
            error: errorText(detail || `Request failed: ${response.status}`),
            status: response.status,
        };
    }
    try {
        return { ok: true, data: await response.json() };
    } catch (error) {
        return { ok: false, error: errorText(error), status: 0 };
    }
};

/** `POST /workspaces` and `PATCH /workspaces/{id}` answer 409 only for a duplicate name. */
const NAME_TAKEN_STATUS = 409;

/**
 * Flag the one refusal callers handle differently: a workspace name that is
 * already taken belongs under the name field, not in a toast.
 */
const withNameConflict = (result) =>
    result.ok ? result : { ...result, nameTaken: result.status === NAME_TAKEN_STATUS };

const cleanText = (value) => {
    const text = typeof value === 'string' ? value.trim() : '';
    return text || null;
};

/**
 * Creator-only: create the caller's first workspace.  `company` and `logo`
 * (a base64 image data URL, already resized client-side) are optional and
 * stored on the workspace document for branding.  On success the backend
 * re-issues the session cookie with the new `workspace_id`; resolves
 * `{ ok: true, data: { workspace } }`.
 */
export const createWorkspace = ({ name, company = null, logo = null }) =>
    post('workspaces', {
        name: String(name || '').trim(),
        company: cleanText(company),
        logo: cleanText(logo),
    }).then(withNameConflict);

/**
 * Admin/God: partially update a workspace (Settings > Workspace tab).
 * Only the keys that are passed are sent — `name` (trimmed, must be
 * non-empty) and/or `logo` (resized data URL; `null` removes it).  Resolves
 * `{ ok: true, data: { workspace } }` or `{ ok: false, error, status }` (also
 * when nothing was passed, so callers can treat a no-op as a failed save).
 */
export const updateWorkspace = (workspaceId, fields = {}) => {
    const body = {};
    if (fields.name !== undefined) body.name = String(fields.name ?? '').trim();
    if (fields.logo !== undefined) body.logo = cleanText(fields.logo);
    if (!workspaceId || Object.keys(body).length === 0) {
        return Promise.resolve({ ok: false, error: 'Nothing to save.', status: 0, nameTaken: false });
    }
    return patch(`workspaces/${encodeURIComponent(workspaceId)}`, body).then(withNameConflict);
};

const PROFILE_TEXT_FIELDS = ['name', 'country', 'picture'];
const PROFILE_LIST_FIELDS = ['improve_goals', 'model_audience'];

/**
 * Save onboarding answers on the caller's user document.  Only the keys that
 * are passed are sent, so each wizard step can persist its own slice:
 *
 * - `name` / `country` / `picture` — trimmed text (`picture` is a data URL);
 *   the backend flags `name` and `picture` as user-edited so the next login
 *   no longer overwrites them from the identity provider.
 * - `improve_goals` / `model_audience` — arrays of option labels.
 * - `completed_step` — highest finished wizard step (0–5), used to resume.
 * - `accept_terms` — `true` stamps `onboarding.terms_accepted_at`.
 *
 * Resolves `{ ok: true, data: { success, user } }`.
 */
export const updateProfile = (fields = {}) => {
    const body = {};
    for (const key of PROFILE_TEXT_FIELDS) {
        if (fields[key] !== undefined) body[key] = cleanText(fields[key]);
    }
    for (const key of PROFILE_LIST_FIELDS) {
        if (Array.isArray(fields[key])) body[key] = fields[key].map((value) => String(value)).filter(Boolean);
    }
    if (fields.completed_step !== undefined) body.completed_step = Number(fields.completed_step);
    if (fields.accept_terms !== undefined) body.accept_terms = Boolean(fields.accept_terms);
    return post('users/set-profile', body);
};
